import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import TemplateEditor from '../components/campaign/TemplateEditor';
import ConditionEditor from '../components/campaign/ConditionEditor';
import Icons from '../components/campaign/Icons';
import { campaignTemplates } from '../utils/campaignTemplates';
import PageHeader from '../components/PageHeader';
import { Plus, ChevronLeft, ChevronRight, ArrowLeft, Settings, Trash2, Power, Sparkles } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

/**
 * CampaignManagement modern dashboard for Easychat.
 * Handles listing, creating, deleting campaigns and managing the active live campaign using is_active flag.
 */
const CampaignManagement = () => {
    const [view, setView] = useState('list'); // 'list' | 'hub' | 'editor' | 'conditions'
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const { showToast } = useToast();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const CAMPAIGNS_PER_PAGE = 10;

    const [customParams, setCustomParams] = useState([]);

    useEffect(() => {
        fetchData();
        const loadCustomParams = async () => {
            try {
                const config = await api.getWidgetConfig();
                if (config && config.custom_parameters) {
                    setCustomParams(config.custom_parameters);
                }
            } catch (e) {
                console.error('Failed to load custom parameters:', e);
            }
        };
        loadCustomParams();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const campaignsList = await api.getCampaigns();
            const flattened = (campaignsList || []).map(c => ({
                ...(c.data || {}),
                id: c.id,
                priority: c.priority,
                is_active: c.is_active
            }));
            setCampaigns(flattened);
            setError(null);
        } catch (err) {
            setError('Failed to fetch campaigns. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => setView('hub');

    const handleSelectTemplate = (template) => {
        setActiveTemplate({
            ...template,
            id: null,
            name: `${template.name} Campaign`,
            previewButton: template.previewButton || 'Learn More',
            priority: 0
        });
        setView('editor');
    };

    const handleEdit = (campaign) => {
        setActiveTemplate({ ...campaign });
        setView('editor');
    };

    const handleDeleteRequest = (id) => {
        setCampaignToDelete(id);
        setIsDeleteModalOpen(true);
    };
 
    const confirmDelete = async () => {
        if (!campaignToDelete) return;
        setIsDeleting(true);
        try {
            await api.deleteCampaign(campaignToDelete);
            setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete));
            showToast('Campaign deleted successfully', 'success');
            setIsDeleteModalOpen(false);
        } catch (err) {
            showToast('Failed to delete campaign.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleLive = async (campaignId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            await api.updateCampaign(campaignId, { is_active: newStatus });
            setCampaigns(prev => prev.map(c => 
                c.id === campaignId ? { ...c, is_active: newStatus } : c
            ));
            showToast(`Campaign ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        } catch (err) {
            showToast('Failed to update campaign status.', 'error');
        }
    };

    const handleSaveCampaign = async (finalData) => {
        try {
            const payload = { 
                ...finalData, 
                priority: finalData.conditions?.priority || 0 
            };
            
            if (finalData.id) {
                await api.updateCampaign(finalData.id, payload);
            } else {
                await api.createCampaign(payload);
            }
            await fetchData();
            setView('list');
            setActiveTemplate(null);
            showToast('Campaign saved successfully!', 'success');
        } catch (err) {
            showToast('Failed to save campaign: ' + (err.message || 'Unknown error'), 'error');
        }
    };

    /* ── Template Hub View ── */
    if (view === 'hub') {
        return (
            <div className="animate-fadeIn p-10 max-w-5xl mx-auto">
                <div className="flex items-center gap-4 mb-10">
                    <button
                        onClick={() => setView('list')}
                        className="w-9 h-9 flex items-center justify-center border border-gray-100 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 transition-all"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-gray-900 tracking-tighter uppercase">Select a Template</h1>
                        <p className="text-[11px] font-medium text-gray-400 mt-0.5">Choose a starting point for your new campaign.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Custom Campaign Card */}
                    <div
                        onClick={() => handleSelectTemplate({ name: 'New Campaign', previewTitle: 'Welcome!', previewContent: 'Edit your content here...', bgColor: '#f3f4f6', image: '✨' })}
                        className="group bg-white rounded-[28px] border border-gray-100 p-6 cursor-pointer transition-all hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 hover:-translate-y-1 flex items-center gap-6"
                    >
                        <div className="w-16 h-16 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-center text-3xl flex-shrink-0 group-hover:scale-110 transition-transform">
                            ✨
                        </div>
                        <div>
                            <h3 className="text-xs font-black uppercase tracking-widest text-gray-900 mb-1">Custom Campaign</h3>
                            <p className="text-[11px] font-medium text-gray-400">Start from scratch with a blank canvas.</p>
                        </div>
                    </div>

                    {/* Pre-built Templates */}
                    {campaignTemplates.map(tmp => (
                        <div 
                            key={tmp.id}
                            onClick={() => handleSelectTemplate(tmp)}
                            className="group bg-white rounded-[28px] border border-gray-100 cursor-pointer transition-all hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 hover:-translate-y-1 flex overflow-hidden"
                        >
                            <div
                                className="w-36 flex items-center justify-center text-5xl flex-shrink-0"
                                style={{ background: tmp.bgColor }}
                            >
                                <span className="group-hover:scale-110 transition-transform">{tmp.image}</span>
                            </div>
                            <div className="p-6 flex-1">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">{tmp.name}</h3>
                                    {tmp.badge && (
                                        <span className="text-[9px] bg-red-50 text-red-500 px-2 py-0.5 rounded-lg font-black uppercase tracking-wider">
                                            {tmp.badge}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[11px] font-medium text-gray-400 leading-relaxed mb-3">{tmp.description}</p>
                                <ul className="space-y-1">
                                    {tmp.highlights.slice(0, 2).map((h, i) => (
                                        <li key={i} className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                                            <span className="w-1 h-1 bg-gray-300 rounded-full" /> {h}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (view === 'editor') {
        return (
            <TemplateEditor 
                template={activeTemplate} 
                onBack={() => setView(activeTemplate.id ? 'list' : 'hub')} 
                onNext={(data) => {
                    setActiveTemplate(data);
                    setView('conditions');
                }} 
            />
        );
    }

    if (view === 'conditions') {
        return (
            <ConditionEditor 
                campaignData={activeTemplate} 
                customParams={customParams}
                onBack={() => setView('editor')} 
                onSave={handleSaveCampaign} 
            />
        );
    }

    /* ── Main List View ── */
    return (
        <div className="animate-fadeIn p-8 max-w-5xl mx-auto">
            <PageHeader 
              title="Campaign Orchestration"
              description="Boost engagement with automated prompts, targeted offers, and neural behavioral triggers."
              actions={
                <button 
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 px-8 py-2.5 bg-gray-900 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black transition-all shadow-xl shadow-gray-200 active:scale-95"
                >
                    <Plus size={14} /> Forge New Campaign
                </button>
              }
            />

            {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                    <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin mb-4" />
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Loading Campaigns...</p>
                </div>
            ) : error ? (
                <div className="text-center p-6 bg-red-50 text-red-500 rounded-2xl border border-red-100 text-sm font-medium">{error}</div>
            ) : campaigns.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-32 bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 animate-fadeIn">
                    <div className="w-16 h-16 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-center mb-6">
                        <Icons.Target size={28} className="text-gray-300" />
                    </div>
                    <h3 className="text-sm font-black text-gray-900 mb-2 uppercase tracking-widest">No Campaigns Found</h3>
                    <p className="text-gray-400 max-w-xs mx-auto text-xs mb-8 font-medium">
                        Initiate your first engagement protocol to begin orchestrating visitor interaction.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 p-8">
                    <div className="max-h-[600px] overflow-y-auto custom-scrollbar pr-2 pb-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {campaigns.slice((currentPage - 1) * CAMPAIGNS_PER_PAGE, currentPage * CAMPAIGNS_PER_PAGE).map(campaign => (
                                <div
                                    key={campaign.id}
                                    className="bg-white rounded-[24px] border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all group"
                                >
                                    {/* Image */}
                                    <div className="h-36 flex items-center justify-center relative" style={{ background: campaign.bgColor || '#fecaca' }}>
                                        {campaign.image && campaign.image.startsWith('data:image') ? (
                                            <img src={campaign.image} alt="Campaign" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-5xl">{campaign.image || '🏷️'}</span>
                                        )}
                                        
                                        {/* Status badge */}
                                        <div className="absolute top-3 left-3">
                                            <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider text-white ${
                                                campaign.is_active ? 'bg-emerald-500' : 'bg-gray-900/40 backdrop-blur-sm'
                                            }`}>
                                                {campaign.is_active ? 'Live' : 'Draft'}
                                            </span>
                                        </div>

                                        {/* Priority badge */}
                                        {campaign.priority > 0 && (
                                            <div className="absolute top-3 right-3">
                                                <span className="px-2 py-1 rounded-lg text-[9px] bg-indigo-600 text-white font-black uppercase tracking-wider">
                                                    P{campaign.priority}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="p-5">
                                        <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-900 mb-1 truncate">
                                            {campaign.name}
                                        </h3>
                                        <p className="text-[11px] font-medium text-gray-400 mb-5 line-clamp-2 h-8">
                                            {campaign.previewTitle}
                                        </p>

                                        {/* Actions */}
                                        <div className="flex gap-2 border-t border-gray-50 pt-4">
                                            <button 
                                                onClick={() => handleToggleLive(campaign.id, campaign.is_active)}
                                                className={`flex-1 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all border ${
                                                    campaign.is_active 
                                                    ? 'bg-white border-gray-100 text-red-500 hover:bg-red-50 hover:border-red-100' 
                                                    : 'bg-gray-900 border-gray-900 text-white hover:bg-black'
                                                }`}
                                            >
                                                {campaign.is_active ? 'Deactivate' : 'Go Live'}
                                            </button>
                                            <button 
                                                onClick={() => handleEdit(campaign)}
                                                className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 transition-all"
                                            >
                                                <Settings size={14} />
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteRequest(campaign.id)}
                                                className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-100 text-gray-300 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pagination */}
                    {Math.ceil(campaigns.length / CAMPAIGNS_PER_PAGE) > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-50">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-2">
                                Page {currentPage} of {Math.ceil(campaigns.length / CAMPAIGNS_PER_PAGE)}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(campaigns.length / CAMPAIGNS_PER_PAGE)))}
                                    disabled={currentPage === Math.ceil(campaigns.length / CAMPAIGNS_PER_PAGE)}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
 
            {/* Delete Modal */}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Campaign"
                message="Are you sure you want to permanently remove this campaign? This action cannot be undone."
                loading={isDeleting}
            />
        </div>
    );
};

export default CampaignManagement;
